/**
 * Light, dark, or the system's: one attribute on <html> (data-theme) that every token in
 * index.css follows. index.html sets it before the first paint from the same storage key, so
 * nothing flashes; this module keeps it right afterwards and lets the settings change it.
 */

export type ThemeSetting = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

export const THEME_KEY = 'ns-theme';
/** Sent on the window whenever the applied theme changes; the detail is the Theme. */
export const THEME_CHANGED = 'noodle:theme-changed';

const THEME_COLOR: Record<Theme, string> = { light: '#F9F6F1', dark: '#0F0F14' };

const isSetting = (value: unknown): value is ThemeSetting => value === 'light' || value === 'dark' || value === 'system';

export const getThemeSetting = (): ThemeSetting => {
    try {
        const stored = localStorage.getItem(THEME_KEY);
        return isSetting(stored) ? stored : 'system';
    } catch {
        return 'system';
    }
};

const systemTheme = (): Theme => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

/** The theme in force for a setting (what "system" means right now). */
export const resolveTheme = (setting: ThemeSetting = getThemeSetting()): Theme => (setting === 'system' ? systemTheme() : setting);

/** The theme the page shows now. */
export const currentTheme = (): Theme => (typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');

/** Writes the theme onto <html> and the browser's own colour (the address bar on a phone). */
export const applyTheme = (setting: ThemeSetting = getThemeSetting()): Theme => {
    const theme = resolveTheme(setting);
    if (typeof document === 'undefined') return theme;
    const root = document.documentElement;
    const changed = root.dataset.theme !== theme;
    root.dataset.theme = theme;
    root.dataset.themeSetting = setting;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = THEME_COLOR[theme];
    if (changed) window.dispatchEvent(new CustomEvent<Theme>(THEME_CHANGED, { detail: theme }));
    return theme;
};

export const setThemeSetting = (setting: ThemeSetting): Theme => {
    try {
        if (setting === 'system') localStorage.removeItem(THEME_KEY);
        else localStorage.setItem(THEME_KEY, setting);
    } catch { /* private mode: the choice lasts for this page */ }
    return applyTheme(setting);
};

/**
 * Keeps the theme in step with the system while the setting is "system", and with other tabs
 * of the app. Returns the function that stops it.
 */
export const watchTheme = (): (() => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onMedia = () => { if (getThemeSetting() === 'system') applyTheme('system'); };
    const onStorage = (event: StorageEvent) => { if (event.key === THEME_KEY || event.key === null) applyTheme(); };
    media.addEventListener('change', onMedia);
    window.addEventListener('storage', onStorage);
    return () => {
        media.removeEventListener('change', onMedia);
        window.removeEventListener('storage', onStorage);
    };
};
